import { effect, inject, Injectable, Injector, runInInjectionContext } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { SavedTreeItem, VariableType } from '@selfai-platform/pipeline-common';
import { AlertService } from '@selfai-platform/shared';
import { ConfirmationService, TreeNode } from 'primeng/api';
import { from, take } from 'rxjs';
import { PENDING_NEW_ITEM_ID, TreeDataItem } from '../models';
import { WorkflowVariablesStore } from '../store';
import { generateRequestId, isGroup, isNewItem, isVariable } from '../utils';
import { VariablesClipboardService, VariablesExportImportService } from './';
import { VariablesEditingStateService } from './variables-editing-state.service';
import { VariablesFormService } from './variables-form.service';
import { VariablesTreeDataService } from './variables-tree-data.service';

@Injectable()
export class VariablesActionsService {
  private readonly store = inject(WorkflowVariablesStore);
  private readonly alertService = inject(AlertService);
  private readonly clipboardService = inject(VariablesClipboardService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly exportImportService = inject(VariablesExportImportService);
  private readonly translateService = inject(TranslateService);
  private readonly injector = inject(Injector);

  private readonly editingStateService = inject(VariablesEditingStateService);
  private readonly formService = inject(VariablesFormService);
  private readonly treeDataService = inject(VariablesTreeDataService);

  addGroup(): void {
    if (!this.editingStateService.isEditable()) return;

    const selectedNode = this.editingStateService.getSelectedNode();
    const parent = selectedNode && isGroup(selectedNode.data) ? selectedNode : selectedNode?.parent || null;

    this.treeDataService.expandParentNode(parent as TreeNode<SavedTreeItem>);
    this.editingStateService.startCreatingNew(VariableType.GROUP, parent?.data?.id);
    this.startEditingNewItem(VariableType.GROUP, parent);
  }

  addVariable(): void {
    if (!this.editingStateService.isEditable()) return;

    const selectedNode = this.editingStateService.getSelectedNode();
    const parent = selectedNode && isGroup(selectedNode.data) ? selectedNode : selectedNode?.parent || null;

    this.treeDataService.expandParentNode(parent as TreeNode<SavedTreeItem>);
    this.editingStateService.startCreatingNew(VariableType.VARIABLE, parent?.data?.id);
    this.startEditingNewItem(VariableType.VARIABLE, parent);
  }

  editItem(data: TreeDataItem): void {
    if (!this.editingStateService.isEditable() || isNewItem(data)) return;

    this.editingStateService.startEditing(data.id);
    this.store.clearError();

    this.formService.patchFormValue({
      key: data.key,
      value: isVariable(data) ? data.value : null,
      description: data.description || '',
      regexRule: isVariable(data) ? data.regexRule || '' : null,
      parentGroup: this.treeDataService.findGroupNodeByKey(data.parentGroupId || ''),
    });

    this.formService.setValidators(isVariable(data) ? VariableType.VARIABLE : VariableType.GROUP);
  }

  deleteItem(data: TreeDataItem): void {
    if (!this.editingStateService.isEditable()) return;

    const itemType = isGroup(data)
      ? this.translateService.instant('workflow.variables.tree.delete.confirmation.group-and-nested-items')
      : this.translateService.instant('workflow.variables.tree.delete.confirmation.variable');

    const message = this.translateService.instant('workflow.variables.tree.delete.confirmation.message', {
      itemType: itemType.toLowerCase(),
      key: data.key,
    });

    this.confirmationService.confirm({
      message: message,
      header: this.translateService.instant('workflow.variables.tree.delete.confirmation.header'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translateService.instant('workflow.variables.tree.delete.confirmation.accept'),
      rejectLabel: this.translateService.instant('workflow.variables.tree.delete.confirmation.reject'),
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-outlined',
      acceptIcon: 'pi pi-trash',
      rejectIcon: 'pi pi-times',
      accept: () => {
        this.store.deleteVariables([data.id]);
      },
    });
  }

  copyPasteItem(data: TreeDataItem): void {
    if (!this.editingStateService.isEditable() || isNewItem(data) || !isVariable(data)) return;

    this.clipboardService.copyPasteVariable(data);
  }

  saveItem(): void {
    if (!this.formService.isFormValid()) return;

    this.store.clearError();

    const editingId = this.editingStateService.getEditingItemId();
    if (!editingId) return;

    const formValue = this.formService.getFormValue();
    const requestId = generateRequestId();

    if (editingId === PENDING_NEW_ITEM_ID) {
      const pendingItem = this.editingStateService.getPendingNewItem();
      if (!pendingItem) return;

      const createRequest = this.formService.buildVariableRequest(formValue, pendingItem.type);
      this.store.createVariable({ requestId, request: createRequest });
    } else {
      const existingItem = this.store.variablesAndGroups().find((item) => item.id === editingId);
      if (!existingItem) return;

      const updateRequest = this.formService.buildVariableRequest(
        formValue,
        isVariable(existingItem) ? VariableType.VARIABLE : VariableType.GROUP,
      );

      this.store.updateVariable({ id: editingId, requestId, request: updateRequest });
    }

    this.handleSaveResponse(requestId);
  }

  cancelEdit(): void {
    this.store.clearError();
    this.editingStateService.cancelEditing();
    this.formService.resetForm();
  }

  exportVariables(workflowId: string): void {
    this.exportImportService.exportVariables(workflowId);
  }

  importVariables(): void {
    this.exportImportService.importVariables();
  }

  copyAbsolutePath(absolutePath: string): void {
    from(window.navigator.clipboard.writeText(absolutePath))
      .pipe(take(1))
      .subscribe(() => {
        this.alertService.success(this.translateService.instant('workflow.variables.tree.copy.path.success'));
      });
  }

  private startEditingNewItem(type: VariableType, parent: TreeNode<TreeDataItem> | null): void {
    this.store.clearError();

    this.formService.patchFormValue({
      key: '',
      value: type === VariableType.VARIABLE ? '' : null,
      description: '',
      regexRule: type === VariableType.VARIABLE ? '' : null,
      parentGroup: parent ? this.treeDataService.findGroupNodeByKey(parent.data.id) : null,
    });

    this.formService.setValidators(type);
  }

  private handleSaveResponse(requestId: string): void {
    runInInjectionContext(this.injector, () => {
      const isCreated = effect(() => {
        const status = this.store.lastRequestStatus();
        if (status?.status === 'fulfilled' && status.requestId === requestId) {
          this.cancelEdit();
          isCreated.destroy();
        } else if (status?.status === 'rejected' && status.requestId === requestId) {
          isCreated.destroy();
        }
      });
    });
  }
}
